import React from 'react'

// Components
import {
    Box,
    Button,
    Flex,
    Heading,
    VStack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import Field from '@app/components/field'
import FieldsetForm from '@app/components/shared/ui/FieldsetForm'
import PhoneField from '@app/components/phone-field'
import ReCAPTCHA from 'react-google-recaptcha'

// Constants
import {RECAPTCHA_SITE_KEY} from '@app/constants'

// Hooks
import {useBespokeContact} from '@app/hooks/use-bespoke-contact'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {useIntl} from 'react-intl'

// Utils
import {isServer} from '@app/utils/utils'

/**
 * React component for the Bespoke Contact Form PD component.
 * Corresponds to `commerce_layouts.pdcBespokeContactForm`
 */
export const PDCBespokeContactForm = () => {
    const form = useForm({mode: 'onSubmit'})
    const intl = useIntl()
    const {onSubmit, formFields, recaptchaRef} = useBespokeContact(form)

    const recaptchaStyle = useBreakpointValue(
        {
            base: 'inline',
            lg: 'bottomright'
        },
        {ssr: isServer}
    )

    const reCaptchaContainerStyle = {
        '.grecaptcha-badge': {
            position: {lg: 'absolute !important'},
            bottom: {lg: '25px !important'}
        }
    }

    return (
        <VStack
            bg="lightBeige"
            p="64px 10px 32px"
            position="relative"
            justifyContent="center"
            overflow="hidden"
            gap={24}
            sx={reCaptchaContainerStyle}
        >
            <Box
                flex="0 0 100%"
                bg="white"
                p={{base: '24px 16px', md: '40px 102px'}}
                borderWidth="1px"
                borderStyle="solid"
                borderColor="borderGray"
                maxW="1100px"
            >
                <Heading size="4xl" textTransform="uppercase" mb={16} textAlign="center">
                    <FormattedMessage
                        id="bespoke_contact.form.title"
                        defaultMessage="Tell us about your project"
                    />
                </Heading>

                <FieldsetForm
                    id="bespoke-contact-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    legendText={intl.formatMessage({
                        id: 'bespoke_contact.form.legend',
                        defaultMessage: 'Contact information'
                    })}
                >
                    <Flex direction={{base: 'column', md: 'row'}} gap={24}>
                        <Field {...formFields.firstName} />
                        <Field {...formFields.lastName} />
                    </Flex>

                    <Flex direction={{base: 'column', md: 'row'}} gap={24}>
                        <Field {...formFields.email} />
                        <PhoneField {...formFields.phone} />
                    </Flex>

                    <Flex direction={{base: 'column', md: 'row'}} gap={24}>
                        <Field {...formFields.companyName} />
                        <Field {...formFields.project} />
                    </Flex>

                    <Field {...formFields.comments} />

                    <Button
                        type="submit"
                        isLoading={form.isSubmitting}
                        variant="primary"
                        width="full"
                        textTransform="uppercase"
                    >
                        <FormattedMessage
                            id="bespoke_contact.form.submit.label"
                            defaultMessage="Request now"
                        />
                    </Button>
                </FieldsetForm>
            </Box>

            <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                size="invisible"
                badge={recaptchaStyle}
            />
        </VStack>
    )
}
